import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiRefreshCw, FiDollarSign, FiMonitor } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency } from '../../utils/formatters';
import { getCashAccountBalance } from '../../utils/posShiftHelper';

const POSBranchCashReport = () => {
  const { activeCompany } = useAuth();
  const [rows, setRows] = useState([]); // { counter, balance }
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    setHasLoaded(true);
    try {
      const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'posCounters'));
      const counters = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isActive !== false);
      const withBal = await Promise.all(counters.map(async (c) => ({
        counter: c,
        balance: c.cashAccountId ? await getCashAccountBalance(activeCompany.id, c.cashAccountId) : 0
      })));
      setRows(withBal);
    } catch (e) {
      console.error('Error loading branch cash position:', e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { load(); }, [load]);

  // Group counters by branch
  const groups = useMemo(() => {
    const map = {};
    rows.forEach(({ counter, balance }) => {
      const key = counter.branchId || 'none';
      if (!map[key]) map[key] = { branchName: counter.branchName || 'No Branch', items: [], total: 0 };
      map[key].items.push({ counter, balance });
      map[key].total += Number(balance) || 0;
    });
    return Object.values(map).sort((a, b) => a.branchName.localeCompare(b.branchName));
  }, [rows]);

  const grandTotal = useMemo(() => rows.reduce((s, r) => s + (Number(r.balance) || 0), 0), [rows]);

  return (
    <PageLayout pageTitle="Branch Cash Position" breadcrumb="Reports / Sale Reports / Branch Cash Position">
      <TopBar>
        <Note>Live cash balance of each POS counter's cash account (GL), grouped by branch.</Note>
        <RefreshBtn onClick={load} disabled={loading}><FiRefreshCw /> {loading ? 'Loading...' : 'Refresh'}</RefreshBtn>
      </TopBar>

      {hasLoaded && (
        <SummaryCards>
          <Card>
            <Icon color={colors.success.main}><FiDollarSign /></Icon>
            <div>
              <CardLabel>Total POS Cash</CardLabel>
              <CardValue>{formatCurrency(grandTotal)}</CardValue>
            </div>
          </Card>
          <Card>
            <Icon color={colors.primary.main}><FiMonitor /></Icon>
            <div>
              <CardLabel>Counters</CardLabel>
              <CardValue>{rows.length}</CardValue>
            </div>
          </Card>
        </SummaryCards>
      )}

      <ContentCard>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : rows.length === 0 ? (
          <Empty>No POS counters found.</Empty>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>Counter</Th>
                <Th>Cash Account</Th>
                <Th align="right">Cash Balance</Th>
              </Tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.branchName}>
                  <BranchRow><Td colSpan={3}>{g.branchName}</Td></BranchRow>
                  {g.items.map(({ counter, balance }) => (
                    <Tr key={counter.id}>
                      <Td style={{ paddingLeft: 28 }}>{counter.counterName} <Code>({counter.counterCode})</Code></Td>
                      <Td>{counter.cashAccountCode || '-'} {counter.cashAccountName ? `— ${counter.cashAccountName}` : ''}</Td>
                      <Amount align="right">{formatCurrency(balance)}</Amount>
                    </Tr>
                  ))}
                  <SubtotalRow>
                    <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>{g.branchName} Total</Td>
                    <Amount align="right" style={{ fontWeight: 700 }}>{formatCurrency(g.total)}</Amount>
                  </SubtotalRow>
                </React.Fragment>
              ))}
              <GrandRow>
                <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>Total POS Cash</Td>
                <Amount align="right" style={{ fontWeight: 700 }}>{formatCurrency(grandTotal)}</Amount>
              </GrandRow>
            </tbody>
          </Table>
        )}
      </ContentCard>
    </PageLayout>
  );
};

const TopBar = styled.div` display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 16px; flex-wrap: wrap; `;
const Note = styled.div` font-size: 13px; color: ${colors.text.secondary}; `;
const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 8px; padding: 10px 18px;
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${colors.borderRadius.md}; font-size: 14px; font-weight: 500; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SummaryCards = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-bottom: 20px; `;
const Card = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm};
  padding: 20px; display: flex; align-items: center; gap: 16px;
`;
const Icon = styled.div`
  width: 48px; height: 48px; border-radius: ${colors.borderRadius.md};
  background: ${props => props.color}15; color: ${props => props.color};
  display: flex; align-items: center; justify-content: center; font-size: 20px;
`;
const CardLabel = styled.div` font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 4px; `;
const CardValue = styled.div` font-size: 22px; font-weight: 600; color: ${colors.text.primary}; `;
const ContentCard = styled.div` background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden; `;
const Table = styled.table` width: 100%; border-collapse: collapse; `;
const Tr = styled.tr` border-bottom: 1px solid ${colors.neutral[100]}; &:hover { background: ${colors.neutral[50]}; } `;
const Th = styled.th`
  padding: 12px 16px; text-align: ${props => props.align || 'left'};
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid ${colors.neutral[200]};
`;
const Td = styled.td` padding: 12px 16px; font-size: 14px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'}; `;
const Amount = styled(Td)` font-family: 'SF Mono', 'Consolas', monospace; font-size: 13px; `;
const Code = styled.span` color: ${colors.text.tertiary}; font-size: 12px; `;
const BranchRow = styled.tr` background: ${colors.neutral[100]}; td { font-weight: 700; color: ${colors.text.primary}; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; } `;
const SubtotalRow = styled.tr` background: ${colors.neutral[50]}; `;
const GrandRow = styled.tr` background: ${colors.primary.main}10; border-top: 2px solid ${colors.primary.main}40; `;
const Empty = styled.div` text-align: center; padding: 50px; color: ${colors.text.secondary}; `;

export default POSBranchCashReport;
